import Dexie, { type EntityTable } from 'dexie';
import type {
  AppMetadata,
  AppSettings,
  Exercise,
  PlanExercise,
  ScheduledWorkout,
  SetEntry,
  WorkoutExercise,
  WorkoutPlan,
  WorkoutSession,
  UserProfile,
  ExercisePreference,
  BodyMeasurement
} from './types';
import { baseExerciseCatalog, SEED_VERSION } from './seed/catalog';
import { normalizeExerciseName } from '../shared/utils/number';

export const DATABASE_NAME = 'trenirovki-pwa';
export const DATABASE_SCHEMA_VERSION = 4;
export const SETTINGS_ID = '00000000-0000-0000-0000-000000000001';
export const METADATA_ID = '00000000-0000-0000-0000-000000000002';
export const APP_VERSION = '2.0.0';

const storesV1 = {
  exercises: '&id, &normalizedName, muscleGroup, isCustom, isArchived, updatedAt',
  workoutPlans: '&id, name, isArchived, updatedAt',
  planExercises: '&id, planId, exerciseId, [planId+orderIndex], updatedAt',
  workoutSessions: '&id, workoutDate, status, sourcePlanId, updatedAt',
  workoutExercises: '&id, workoutSessionId, exerciseIdSnapshot, [workoutSessionId+orderIndex], updatedAt',
  setEntries: '&id, workoutExerciseId, [workoutExerciseId+orderIndex], isCompleted, updatedAt',
  scheduledWorkouts: '&id, scheduledDate, planId, status, linkedWorkoutSessionId, updatedAt',
  appSettings: '&id, updatedAt'
};

const storesV2 = { ...storesV1, appMetadata: '&id, schemaVersion, seedVersion, updatedAt' };
const storesV3 = {
  ...storesV2,
  exercises: '&id, name, &normalizedName, muscleGroup, isCustom, isArchived, updatedAt'
};

export class TrainingDatabase extends Dexie {
  exercises!: EntityTable<Exercise, 'id'>;
  workoutPlans!: EntityTable<WorkoutPlan, 'id'>;
  planExercises!: EntityTable<PlanExercise, 'id'>;
  workoutSessions!: EntityTable<WorkoutSession, 'id'>;
  workoutExercises!: EntityTable<WorkoutExercise, 'id'>;
  setEntries!: EntityTable<SetEntry, 'id'>;
  scheduledWorkouts!: EntityTable<ScheduledWorkout, 'id'>;
  appSettings!: EntityTable<AppSettings, 'id'>;
  appMetadata!: EntityTable<AppMetadata, 'id'>;
  userProfiles!: EntityTable<UserProfile, 'id'>;
  exercisePreferences!: EntityTable<ExercisePreference, 'id'>;
  bodyMeasurements!: EntityTable<BodyMeasurement, 'id'>;

  constructor(name = DATABASE_NAME) {
    super(name);
    this.version(1).stores(storesV1);
    this.version(2).stores(storesV2).upgrade(async transaction => {
      const exercises = transaction.table<Exercise, string>('exercises');
      await exercises.toCollection().modify(exercise => {
        exercise.normalizedName = normalizeExerciseName(exercise.name);
      });
      const settingsTable = transaction.table<AppSettings, string>('appSettings');
      await settingsTable.toCollection().modify(settings => {
        settings.defaultRestSeconds ??= 90;
        settings.lastScheduledReminderTime ??= '09:00';
        settings.theme ??= 'system';
        settings.lastBackupAt ??= null;
        settings.storagePersistenceRequestedAt ??= null;
        settings.onboardingDismissed ??= false;
      });
    });
    this.version(3).stores(storesV3);
    this.version(4).stores({
      ...storesV3,
      userProfiles: '&id, updatedAt',
      exercisePreferences: '&id, &exerciseId, updatedAt',
      bodyMeasurements: '&id, &date, updatedAt'
    });
  }
}

export const db = new TrainingDatabase();

export async function initializeDatabase(database: TrainingDatabase = db): Promise<void> {
  await database.open();
  const now = new Date().toISOString();
  await database.transaction('rw', database.exercises, database.appSettings, database.appMetadata, async () => {
    const metadata = await database.appMetadata.get(METADATA_ID);
    const currentExercises = await database.exercises.toArray();
    const ids = new Set(currentExercises.map(item => item.id));
    const names = new Set(currentExercises.map(item => item.normalizedName));
    const missing = baseExerciseCatalog(now).filter(item => !ids.has(item.id) && !names.has(item.normalizedName));
    if (missing.length > 0) await database.exercises.bulkAdd(missing);

    if (!(await database.appSettings.get(SETTINGS_ID))) {
      await database.appSettings.add({
        id: SETTINGS_ID,
        defaultRestSeconds: 90,
        lastScheduledReminderTime: '09:00',
        theme: 'system',
        lastBackupAt: null,
        storagePersistenceRequestedAt: null,
        onboardingDismissed: false,
        createdAt: now,
        updatedAt: now
      });
    }
    await database.appMetadata.put({
      id: METADATA_ID,
      schemaVersion: DATABASE_SCHEMA_VERSION,
      appVersion: APP_VERSION,
      firstOpenedAt: metadata?.firstOpenedAt ?? now,
      seedVersion: SEED_VERSION,
      createdAt: metadata?.createdAt ?? now,
      updatedAt: now
    });
  });
}
